import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../../FishFinderBuilder.css';
import './CompareScreensV2.css';
import { getBestPriceByProductId } from '../../utils/api';
import {
  COMPARE_V2_COMPARE_IDS,
  getRecommendedIdFromLocationSearch,
} from '../../data/compareScreensV2Models';
import {
  loadCompareSelectedIds,
  persistCompareSelectedIds,
} from '../../utils/compareScreensV2Session';
import { fetchCompareScreensProducts } from '../../utils/supabaseCompareScreens';
import {
  COMPARE_V2_LIVESCOPE_SUMMARY,
  formatCompareDetailControls,
  formatCompareDetailPorts,
  formatCompareDetailVideo,
} from '../../data/compareScreensV2CompareDetail';

const MAX_COMPARE_SLOTS = 3;

/**
 * Touch taps focus the button after pointerup; synchronous blur runs too early and focus returns,
 * leaving :focus-visible rings stuck until the next tap. Defer blur until after the browser
 * finishes the tap focus cycle. Keyboard (Tab/Space) does not use touch pointer types.
 */
function scheduleBlurAfterTouchTap(el) {
  if (!el) return;
  const run = () => {
    el.blur();
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      run();
      setTimeout(run, 0);
    });
  });
}

const COMPARE_V2_TOUCH_PRIMARY_BLUR_PROPS = {
  onPointerUp(e) {
    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      scheduleBlurAfterTouchTap(e.currentTarget);
    }
  },
  onTouchEnd(e) {
    scheduleBlurAfterTouchTap(e.currentTarget);
  },
};

const TILE_FILTER_GROUPS = [
  {
    id: 'size',
    label: 'Screen size',
    chips: [
      { key: 'size-7', label: '7 inch' },
      { key: 'size-9', label: '9 inch' },
      { key: 'size-10', label: '10 inch' },
      { key: 'size-12', label: '12 inch+' },
    ],
  },
  {
    id: 'controls',
    label: 'Controls',
    chips: [
      { key: 'control-touch', label: 'Touchscreen' },
      { key: 'control-keypad', label: 'Keypad + touchscreen' },
    ],
  },
  {
    id: 'video',
    label: 'Video',
    chips: [{ key: 'video-hdmi', label: 'HDMI out' }],
  },
];

/** Map display diagonal (inches) to filter bucket used by tile chips. */
function screenSizeInchesFromSpecs(specs) {
  const d = specs?.display_diagonal_inches_exact;
  if (d == null) return null;
  const n = Number(d);
  if (!Number.isFinite(n)) return null;
  if (n < 8) return 7;
  if (n < 9.5) return 9;
  if (n < 11) return 10;
  return 12;
}

/**
 * Shape Supabase compare `specs` into a row for filters and formatCompareDetail* helpers.
 */
function specsToMatrixRow(specs) {
  if (!specs) return null;
  const out = {};
  if (typeof specs.keypad_supported === 'boolean') {
    out.controlMethod = specs.keypad_supported ? 'Keypad' : 'Touchscreen';
  } else if (typeof specs.touch_supported === 'boolean' && specs.touch_supported) {
    out.controlMethod = 'Touchscreen';
  }
  const port = specs.garmin_marine_network_port_count;
  if (port != null && port !== '' && Number.isFinite(Number(port))) {
    out.networkPorts = String(Number(port));
  }
  if (typeof specs.hdmi_out_supported === 'boolean') {
    out.hdmiOut = specs.hdmi_out_supported ? 'Yes' : 'No';
  }
  if (typeof specs.hdmi_in_supported === 'boolean') {
    out.hdmiIn = specs.hdmi_in_supported ? 'Yes' : 'No';
  }
  if (typeof specs.bnc_composite_in_supported === 'boolean') {
    out.bncVideoIn = specs.bnc_composite_in_supported ? 'Yes' : 'No';
  }
  return out;
}

function passesTileFilters(opt, active, catalogRow) {
  if (!catalogRow) return false;
  const inches = opt.screenSizeInches;

  const sizeKeys = ['size-7', 'size-9', 'size-10', 'size-12'].filter((k) => active.has(k));
  if (sizeKeys.length > 0) {
    if (inches == null) return false;
    let ok = false;
    if (active.has('size-7') && inches === 7) ok = true;
    if (active.has('size-9') && inches === 9) ok = true;
    if (active.has('size-10') && inches === 10) ok = true;
    if (active.has('size-12') && inches >= 12) ok = true;
    if (!ok) return false;
  }

  const wantTouch = active.has('control-touch');
  const wantKeypad = active.has('control-keypad');
  if (wantTouch || wantKeypad) {
    const isKeypad = catalogRow.controlMethod === 'Keypad';
    let ok = false;
    if (wantTouch && wantKeypad) {
      ok = true;
    } else if (wantTouch) {
      ok = !isKeypad;
    } else {
      ok = isKeypad;
    }
    if (!ok) return false;
  }

  if (active.has('video-hdmi') && catalogRow.hdmiOut !== 'Yes') {
    return false;
  }

  return true;
}

function formatMoney(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Live lowest price (same API path as /compare-screens). */
function BestPriceCell({ compareId, entry, compareCatalogById }) {
  if (entry === 'loading' || entry === undefined) {
    return <span className="compare-v2-price-fallback">Loading…</span>;
  }
  if (entry && typeof entry.price === 'number') {
    return (
      <div className="compare-v2-price-block">
        <div className="compare-v2-price-value">${formatMoney(entry.price)}</div>
        {entry.msrpSavings && (
          <div className="compare-v2-price-save">
            Save ${formatMoney(Number(entry.msrpSavings.amount))} vs {entry.msrpSavings.reference || 'MSRP'}
          </div>
        )}
      </div>
    );
  }
  const cat = compareCatalogById[compareId];
  const msrp = cat?.msrp != null ? cat.msrp : null;
  return (
    <span className="compare-v2-price-fallback">
      Unavailable{msrp != null ? ` · MSRP $${formatMoney(msrp)}` : ''}
    </span>
  );
}

function DealLinkCell({ compareId, compareCatalogById }) {
  const cat = compareCatalogById[compareId];
  const slug = cat?.slug != null && cat.slug !== '' ? cat.slug : null;
  if (slug) {
    return (
      <Link
        to={`/simple-products/${slug}`}
        className="compare-v2-deal-cta compare-v2-deal-cta-primary"
      >
        Compare Prices
      </Link>
    );
  }
  return <span className="compare-v2-price-fallback">N/A</span>;
}

function getPanelCompareCtaLabel(selectedCount) {
  if (selectedCount === 1) return 'See details';
  if (selectedCount === 2) return 'Compare these';
  return 'Compare these 3';
}

function CompareScreensV2Compare() {
  const location = useLocation();

  const [compareCatalogLoaded, setCompareCatalogLoaded] = useState(false);
  const [compareIdToProductId, setCompareIdToProductId] = useState({});
  const [compareCatalogById, setCompareCatalogById] = useState({});

  const [selectedIds, setSelectedIds] = useState(() =>
    loadCompareSelectedIds(
      typeof window !== 'undefined' ? window.location.search : '',
      COMPARE_V2_COMPARE_IDS,
    ),
  );

  const resultsRef = useRef(null);
  const selectSectionRef = useRef(null);
  const selectHighlightTimerRef = useRef(null);
  const [highlightTileSelection, setHighlightTileSelection] = useState(false);
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [activeFilterKeys, setActiveFilterKeys] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCompareScreensProducts();
        if (cancelled) return;
        const map = {};
        const catalogById = {};
        for (const row of data) {
          if (row.id) {
            catalogById[row.id] = row;
          }
          if (row.id && row.product_id) {
            map[row.id] = row.product_id;
          }
        }
        console.log(
          '[CompareScreensV2] supabase coverage',
          COMPARE_V2_COMPARE_IDS.map((id) => ({
            compare_id: id,
            source: catalogById[id] ? 'supabase' : 'fallback',
          })),
        );
        setCompareIdToProductId(map);
        setCompareCatalogById(catalogById);
      } catch (err) {
        if (!cancelled) {
          console.log('[CompareScreensV2] fetchCompareScreensProducts error', err);
        }
      } finally {
        if (!cancelled) setCompareCatalogLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!compareCatalogLoaded) return;
    setSelectedIds((prev) => prev.filter((id) => Boolean(compareCatalogById[id])));
  }, [compareCatalogLoaded, compareCatalogById]);

  const options = useMemo(() => {
    return COMPARE_V2_COMPARE_IDS.map((id) => {
      const cat = compareCatalogById[id];
      if (!cat) return null;
      return {
        id,
        screenSizeInches: screenSizeInchesFromSpecs(cat.specs),
      };
    }).filter(Boolean);
  }, [compareCatalogById]);

  const activeFilterSet = useMemo(() => new Set(activeFilterKeys), [activeFilterKeys]);

  const visibleTileOptions = useMemo(() => {
    return options.filter((opt) => {
      const specs = compareCatalogById[opt.id]?.specs;
      const catalog = specsToMatrixRow(specs);
      return passesTileFilters(opt, activeFilterSet, catalog);
    });
  }, [options, activeFilterSet, compareCatalogById]);

  const hasActiveFilters = activeFilterKeys.length > 0;

  const hiddenSelectedCount = useMemo(() => {
    const visible = new Set(visibleTileOptions.map((o) => o.id));
    return selectedIds.filter((id) => !visible.has(id)).length;
  }, [selectedIds, visibleTileOptions]);

  const toggleFilterKey = useCallback((key) => {
    setActiveFilterKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const clearTileFilters = useCallback(() => {
    setActiveFilterKeys([]);
  }, []);

  useEffect(() => {
    persistCompareSelectedIds(selectedIds);
  }, [selectedIds]);

  useEffect(
    () => () => {
      if (selectHighlightTimerRef.current) {
        clearTimeout(selectHighlightTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const id = getRecommendedIdFromLocationSearch(location.search);
    if (!id) return;
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev;
      return [id, ...prev].filter((x, i, a) => a.indexOf(x) === i).slice(0, MAX_COMPARE_SLOTS);
    });
  }, [location.search]);

  const selectedModels = useMemo(
    () => options.filter((o) => selectedIds.includes(o.id)),
    [options, selectedIds],
  );

  const atMax = selectedIds.length >= MAX_COMPARE_SLOTS;
  const hasSelection = selectedIds.length > 0;
  const activeCompareColumnCount = selectedModels.length;

  const columnSlots = useMemo(() => {
    const cols = selectedModels.map((m) => ({ kind: 'model', model: m, key: m.id }));
    while (cols.length < MAX_COMPARE_SLOTS) {
      cols.push({ kind: 'empty', key: `empty-${cols.length}` });
    }
    return cols;
  }, [selectedModels]);

  const [lowestPricesById, setLowestPricesById] = useState({});

  useEffect(() => {
    if (selectedIds.length === 0) {
      setLowestPricesById({});
      return;
    }
    let cancelled = false;
    const ids = selectedIds;
    setLowestPricesById(Object.fromEntries(ids.map((id) => [id, 'loading'])));
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          const productId = compareIdToProductId[id];
          const cat = compareCatalogById[id];
          const msrp = cat?.msrp != null ? cat.msrp : null;
          if (!productId) {
            return [id, null];
          }
          try {
            const rawResult = await getBestPriceByProductId(productId);
            if (!rawResult || typeof rawResult.lowestPrice !== 'number') {
              return [id, null];
            }
            const price = parseFloat(rawResult.lowestPrice.toFixed(2));
            let msrpSavings = null;
            if (msrp && typeof msrp === 'number' && msrp > price) {
              msrpSavings = { amount: (msrp - price).toFixed(2), reference: 'MSRP' };
            }
            const normalizedEntry = {
              price,
              msrpSavings,
              affiliate_url: rawResult.affiliate_url || null,
            };
            return [id, normalizedEntry];
          } catch {
            return [id, null];
          }
        }),
      );
      if (cancelled) return;
      setLowestPricesById(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedIds, compareIdToProductId, compareCatalogById]);

  const scrollToComparison = useCallback(() => {
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE_SLOTS) return prev;
      return [...prev, id];
    });
  };

  const removeModel = useCallback((id) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const focusTileSelectionArea = useCallback(() => {
    selectSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (selectHighlightTimerRef.current) {
      clearTimeout(selectHighlightTimerRef.current);
    }
    setHighlightTileSelection(true);
    selectHighlightTimerRef.current = setTimeout(() => {
      setHighlightTileSelection(false);
      selectHighlightTimerRef.current = null;
    }, 3200);
  }, []);

  const renderHeaderCell = (slot) => {
    if (slot.kind === 'empty') {
      return (
        <th key={slot.key} scope="col" className="compare-v2-matrix-colhead compare-v2-matrix-colhead-empty">
          <button
            type="button"
            className="compare-v2-matrix-add-slot"
            onClick={focusTileSelectionArea}
          >
            <span className="compare-v2-matrix-add-slot-plus" aria-hidden="true">
              +
            </span>
            <span className="compare-v2-matrix-add-slot-text">Add another model</span>
          </button>
        </th>
      );
    }
    const m = slot.model;
    const cat = compareCatalogById[m.id];
    const headImage = cat?.image_url != null && cat?.image_url !== '' ? cat.image_url : null;
    const tileBadge = cat?.profile?.badge_label ?? null;
    const displayName =
      cat?.title != null && String(cat.title).trim() !== '' ? cat.title : '-';
    return (
      <th key={slot.key} scope="col" className="compare-v2-matrix-colhead compare-v2-matrix-colhead-filled">
        <button
          type="button"
          className="compare-v2-matrix-col-remove"
          onClick={(e) => {
            e.preventDefault();
            removeModel(m.id);
          }}
          aria-label={`Remove ${displayName} from comparison`}
        >
          ×
        </button>
        <div className="compare-v2-matrix-colhead-inner">
          {headImage && (
            <span className="compare-v2-matrix-colhead-img-wrap">
              {tileBadge && (
                <span className="compare-v2-matrix-colhead-badge compare-v2-matrix-colhead-badge--overlay">
                  {tileBadge}
                </span>
              )}
              <img src={headImage} alt="" className="compare-v2-matrix-colhead-img" />
            </span>
          )}
          <span className="compare-v2-matrix-colhead-name">{displayName}</span>
        </div>
      </th>
    );
  };

  const renderBodyCell = (slot, cellRender) => {
    if (slot.kind === 'empty') {
      return (
        <td key={`${slot.key}-cell`} className="compare-v2-matrix-cell compare-v2-matrix-cell-slot-empty">
          <span className="compare-v2-matrix-cell-dash">-</span>
        </td>
      );
    }
    return cellRender(slot.model);
  };

  const mergeDetailScreenRowForFormatters = useCallback(
    (compareId) => {
      const specs = compareCatalogById[compareId]?.specs;
      return specsToMatrixRow(specs);
    },
    [compareCatalogById],
  );

  const detailRows = useMemo(
    () => [
      {
        key: 'resolution',
        label: 'Screen resolution',
        get: (m) => {
          const specs = compareCatalogById[m.id]?.specs;
          const w = specs?.resolution_width_px;
          const h = specs?.resolution_height_px;
          if (
            w != null &&
            h != null &&
            !Number.isNaN(Number(w)) &&
            !Number.isNaN(Number(h))
          ) {
            return `${Number(w)} × ${Number(h)}`;
          }
          return '-';
        },
      },
      {
        key: 'livescope',
        label: 'LiveScope / Panoptix compatible',
        get: () => COMPARE_V2_LIVESCOPE_SUMMARY,
      },
      {
        key: 'controls',
        label: 'Controls',
        get: (m) => formatCompareDetailControls(mergeDetailScreenRowForFormatters(m.id)),
      },
      {
        key: 'ports',
        label: 'Ports',
        get: (m) => formatCompareDetailPorts(mergeDetailScreenRowForFormatters(m.id)),
      },
      {
        key: 'video',
        label: 'Video',
        get: (m) => formatCompareDetailVideo(mergeDetailScreenRowForFormatters(m.id)),
      },
    ],
    [compareCatalogById, mergeDetailScreenRowForFormatters],
  );

  const renderDetailBodyCell = (slot, rowKey, getValue) => {
    const preline = rowKey === 'ports' ? ' compare-v2-matrix-cell--preline' : '';
    const cellClass = `compare-v2-matrix-cell compare-v2-matrix-cell-multiline${preline}`;
    if (slot.kind === 'empty') {
      return (
        <td
          key={`${slot.key}-detail-${rowKey}`}
          className={`${cellClass} compare-v2-matrix-cell-slot-empty`}
        >
          <span className="compare-v2-matrix-cell-dash">-</span>
        </td>
      );
    }
    const m = slot.model;
    return (
      <td key={`${m.id}-detail-${rowKey}`} className={cellClass}>
        {getValue(m)}
      </td>
    );
  };

  return (
    <div className="compare-v2-page compare-v2-compare-page">
      <header className="compare-v2-hero compare-v2-compare-hero">
        <h1>Compare Garmin Screens</h1>
        <p className="compare-v2-lede compare-v2-lede-tight">
          Compare specs and prices side by side.
        </p>
      </header>

      <section
        id="compare-v2-select"
        ref={selectSectionRef}
        className={`compare-v2-panel compare-v2-panel-select${highlightTileSelection ? ' compare-v2-panel-select--highlight' : ''}`}
        aria-label="Screen selection"
      >
        {highlightTileSelection && (
          <p className="compare-v2-select-nudge" role="status">
            Tap a model below to add it to the comparison.
          </p>
        )}

        <div className="compare-v2-tile-filters" aria-label="Narrow models">
          {TILE_FILTER_GROUPS.map((group) => (
            <div key={group.id} className="compare-v2-filter-group">
              <span className="compare-v2-filter-group-label">{group.label}</span>
              <div className="compare-v2-filter-chips">
                {group.chips.map((chip) => {
                  const on = activeFilterKeys.includes(chip.key);
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      className={`compare-v2-filter-chip${on ? ' compare-v2-filter-chip--active' : ''}`}
                      onClick={() => toggleFilterKey(chip.key)}
                      {...COMPARE_V2_TOUCH_PRIMARY_BLUR_PROPS}
                      aria-pressed={on}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {hasActiveFilters && (
            <button
              type="button"
              className="compare-v2-filter-clear"
              onClick={clearTileFilters}
              onPointerUp={(e) => {
                if (e.pointerType === 'touch') e.currentTarget.blur();
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="compare-v2-tile-selection-body">
        {hiddenSelectedCount > 0 && (
          <p className="compare-v2-filter-hidden-selected" role="status">
            {hiddenSelectedCount === 1
              ? '1 selected model is hidden by filters. Clear filters to see it in the grid.'
              : `${hiddenSelectedCount} selected models are hidden by filters. Clear filters to see them in the grid.`}
          </p>
        )}

        <p className="compare-v2-tile-grid-hint" aria-live="polite">
          {hasActiveFilters
            ? `Choose up to 3 to compare · Filtered to ${visibleTileOptions.length} of ${options.length}`
            : 'Choose up to 3 to compare'}
        </p>

        {hasSelection && (
          <div className="compare-v2-panel-compare-wrap" aria-live="polite">
            <button
              type="button"
              className="compare-v2-panel-compare-cta"
              onClick={scrollToComparison}
              onPointerUp={(e) => {
                if (e.pointerType === 'touch') e.currentTarget.blur();
              }}
            >
              {getPanelCompareCtaLabel(selectedIds.length)}
            </button>
          </div>
        )}

        <div className="compare-v2-model-grid compare-v2-model-grid-spaced">
          {!compareCatalogLoaded ? (
            <p className="compare-v2-filter-empty" role="status">
              Loading models…
            </p>
          ) : options.length === 0 ? (
            <p className="compare-v2-filter-empty" role="status">
              No compare models are available right now.
            </p>
          ) : visibleTileOptions.length === 0 ? (
            <p className="compare-v2-filter-empty">
              No models match these filters.{' '}
              <button
                type="button"
                className="compare-v2-filter-empty-btn"
                onClick={clearTileFilters}
                onPointerUp={(e) => {
                  if (e.pointerType === 'touch') e.currentTarget.blur();
                }}
              >
                Clear filters
              </button>
            </p>
          ) : (
            visibleTileOptions.map((opt) => {
            const isOn = selectedIds.includes(opt.id);
            const disabled = !isOn && atMax;
            const cat = compareCatalogById[opt.id];
            const tileBadge = cat?.profile?.badge_label ?? null;
            const tileImage =
              cat?.image_url != null && cat?.image_url !== '' ? cat.image_url : null;
            const displayName =
              cat?.title != null && String(cat.title).trim() !== '' ? cat.title : '-';
            return (
              <button
                key={opt.id}
                type="button"
                className={`compare-v2-model-tile${isOn ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                onClick={() => !disabled && toggle(opt.id)}
                {...COMPARE_V2_TOUCH_PRIMARY_BLUR_PROPS}
                disabled={disabled}
                aria-pressed={isOn}
                aria-label={`${isOn ? 'Deselect' : 'Select'} ${displayName}`}
              >
                {tileImage && (
                  <span className="compare-v2-model-tile-img-wrap">
                    {tileBadge && (
                      <span className="compare-v2-model-tile-label compare-v2-model-tile-label--overlay">
                        {tileBadge}
                      </span>
                    )}
                    <img src={tileImage} alt="" className="compare-v2-model-tile-img" />
                  </span>
                )}
                {isOn && (
                  <span className="compare-v2-model-tile-selected-mark" aria-hidden="true">
                    ✓
                  </span>
                )}
                <span className="compare-v2-model-tile-name">{displayName}</span>
              </button>
            );
          })
          )}
        </div>
        </div>
      </section>

      <section
        id="compare-v2-results"
        className={`compare-v2-results compare-v2-results-stack${!hasSelection ? ' compare-v2-results--no-selection' : ''}`}
        ref={resultsRef}
        aria-label="Comparison"
      >
        <h2 className="compare-v2-results-heading">Comparison</h2>

        {!hasSelection && (
          <p className="compare-v2-results-empty-hint" role="status">
            Select models above to compare
          </p>
        )}

        <p className="compare-v2-matrix-hint" aria-hidden="true">
          Swipe to see all columns
        </p>

        <div
          className={`compare-v2-matrix-scroll${!hasSelection ? ' compare-v2-matrix-scroll--muted' : ''}`}
        >
          <table
            className="compare-v2-matrix-table"
            aria-label="Garmin screen comparison"
          >
            <thead>
              <tr>
                <th scope="col" className="compare-v2-matrix-corner" />
                {columnSlots.map((slot) => renderHeaderCell(slot))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  Screen size
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => {
                    const specs = compareCatalogById[m.id]?.specs;
                    const diag = specs?.display_diagonal_inches_exact;
                    const n = diag == null ? NaN : Number(diag);
                    const value = Number.isFinite(n)
                      ? Number.isInteger(n)
                        ? String(n)
                        : String(Number.parseFloat(n.toFixed(1)))
                      : null;
                    const sizeLabel = value != null ? `${value}"` : ':';
                    return (
                      <td
                        key={`${m.id}-size`}
                        className="compare-v2-matrix-cell compare-v2-matrix-cell-screen-size"
                      >
                        <span className="compare-v2-screen-size-value">{sizeLabel}</span>
                        <span className="compare-v2-screen-size-hint"> diagonal</span>
                      </td>
                    );
                  }),
                )}
              </tr>
              <tr>
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  At a glance
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => {
                    const p = compareCatalogById[m.id]?.profile?.boat_use_summary;
                    return (
                      <td
                        key={`${m.id}-tag`}
                        className="compare-v2-matrix-cell compare-v2-matrix-cell-tagline"
                      >
                        {p != null && String(p).trim() !== '' ? p : ':'}
                      </td>
                    );
                  }),
                )}
              </tr>
              <tr>
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  Best for you if
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => {
                    const p = compareCatalogById[m.id]?.profile?.who_its_for;
                    return (
                      <td
                        key={`${m.id}-bf`}
                        className="compare-v2-matrix-cell compare-v2-matrix-cell-multiline"
                      >
                        {p != null && String(p).trim() !== '' ? p : ':'}
                      </td>
                    );
                  }),
                )}
              </tr>
              <tr
                className={
                  hasSelection && !detailExpanded
                    ? 'compare-v2-matrix-row-why compare-v2-matrix-row-why--more-below'
                    : undefined
                }
              >
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  {`Why you'd pick it`}
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => {
                    const p = compareCatalogById[m.id]?.profile?.upgrade_path_note;
                    return (
                      <td
                        key={`${m.id}-why`}
                        className="compare-v2-matrix-cell compare-v2-matrix-cell-multiline"
                      >
                        {p != null && String(p).trim() !== '' ? p : ':'}
                      </td>
                    );
                  }),
                )}
              </tr>
              {hasSelection && !detailExpanded && (
                <tr className="compare-v2-matrix-spec-toggle-row compare-v2-matrix-spec-toggle-row--before">
                  <th
                    scope="row"
                    className="compare-v2-matrix-rowlabel compare-v2-matrix-spec-toggle-ledge"
                    aria-label="Specification controls"
                  />
                  <td colSpan={activeCompareColumnCount} className="compare-v2-matrix-expand-cell">
                    <div className="compare-v2-matrix-expand-cell-inner">
                      <button
                        type="button"
                        className="compare-v2-matrix-spec-toggle"
                        onClick={() => setDetailExpanded(true)}
                        aria-expanded={false}
                        aria-controls="compare-v2-spec-rows"
                        id="compare-v2-spec-expand"
                      >
                        Show more specs <span aria-hidden="true">▼</span>
                      </button>
                    </div>
                  </td>
                  {columnSlots.slice(activeCompareColumnCount).map((slot) => (
                    <td
                      key={`spec-toggle-empty-${slot.key}`}
                      className="compare-v2-matrix-cell compare-v2-matrix-cell-slot-empty compare-v2-matrix-spec-toggle-empty-slot"
                      aria-hidden="true"
                    >
                      <span className="compare-v2-matrix-cell-dash">-</span>
                    </td>
                  ))}
                </tr>
              )}
              {hasSelection &&
                detailExpanded &&
                detailRows.map((row, index) => (
                  <tr
                    key={row.key}
                    id={row.key === detailRows[0].key ? 'compare-v2-spec-rows' : undefined}
                    className={
                      index === detailRows.length - 1
                        ? 'compare-v2-matrix-spec-detail-row--last'
                        : undefined
                    }
                  >
                    <th scope="row" className="compare-v2-matrix-rowlabel">
                      {row.label}
                    </th>
                    {columnSlots.map((slot) => renderDetailBodyCell(slot, row.key, row.get))}
                  </tr>
                ))}
              {hasSelection && detailExpanded && (
                <tr className="compare-v2-matrix-spec-toggle-row compare-v2-matrix-spec-toggle-row--after">
                  <th
                    scope="row"
                    className="compare-v2-matrix-rowlabel compare-v2-matrix-spec-toggle-ledge"
                    aria-label="Specification controls"
                  />
                  <td colSpan={activeCompareColumnCount} className="compare-v2-matrix-expand-cell">
                    <div className="compare-v2-matrix-expand-cell-inner">
                      <button
                        type="button"
                        className="compare-v2-matrix-spec-toggle"
                        onClick={() => setDetailExpanded(false)}
                        aria-expanded={true}
                        aria-controls="compare-v2-spec-rows"
                        id="compare-v2-spec-collapse"
                      >
                        Show less <span aria-hidden="true">▲</span>
                      </button>
                    </div>
                  </td>
                  {columnSlots.slice(activeCompareColumnCount).map((slot) => (
                    <td
                      key={`spec-toggle-empty-${slot.key}`}
                      className="compare-v2-matrix-cell compare-v2-matrix-cell-slot-empty compare-v2-matrix-spec-toggle-empty-slot"
                      aria-hidden="true"
                    >
                      <span className="compare-v2-matrix-cell-dash">-</span>
                    </td>
                  ))}
                </tr>
              )}
              <tr>
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  Current best price
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => (
                    <td
                      key={`${m.id}-live`}
                      className="compare-v2-matrix-cell compare-v2-matrix-cell-price"
                    >
                      <BestPriceCell
                        compareId={m.id}
                        entry={lowestPricesById[m.id]}
                        compareCatalogById={compareCatalogById}
                      />
                    </td>
                  )),
                )}
              </tr>
              <tr>
                <th scope="row" className="compare-v2-matrix-rowlabel">
                  Deal
                </th>
                {columnSlots.map((slot) =>
                  renderBodyCell(slot, (m) => (
                    <td key={`${m.id}-deal`} className="compare-v2-matrix-cell compare-v2-matrix-cell-deal">
                      <DealLinkCell
                        compareId={m.id}
                        compareCatalogById={compareCatalogById}
                      />
                    </td>
                  )),
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default CompareScreensV2Compare;
